import type { SelectDataGridItem } from "@/components/SelectDataGridUtils";
import type { CatParcoExtendedSelectDto } from "@/models/categorie.type";
import type { ExtendedSelectKey, ExtendedSelectRowMap } from "./extendedSelectRowMap";


export type ExtendedSelectColumnConfig<T> = {
  dataField: keyof T & string;
  caption?: string;
  width?: number;
  minWidth?: number;
};

export type ExtendedSelectDefinition<T extends SelectDataGridItem> = {
  path: string;
  displayField: keyof T & string;
  columns: ExtendedSelectColumnConfig<T>[];
  filters?: object;
};

/** Helper to preserve row type when defining a registry entry. */
export function defineExtendedSelect<T extends SelectDataGridItem>(
  config: ExtendedSelectDefinition<T>,
): ExtendedSelectDefinition<T> {
  return config;
}

/**
 * Registry of extended select endpoints (full row objects, DataGrid picker).
 * Key = API path (must exist in ExtendedSelectRowMap).
 */
export const extendedSelectConfig: {
  [K in ExtendedSelectKey]: ExtendedSelectDefinition<ExtendedSelectRowMap[K]>;
} = {
  "parco/categorie/select/extended": defineExtendedSelect<CatParcoExtendedSelectDto>({
    path: "parco/categorie/select/extended",
    displayField: "descrizione",
    columns: [
      { dataField: "codice", caption: "Codice", width: 100 },
      { dataField: "descrizione", caption: "Descrizione", minWidth: 200 },
      { dataField: "raggruppamento", caption: "Raggruppamento", minWidth: 160 },
    ],
  }),

};

export const extendedSelectKeys = Object.keys(
  extendedSelectConfig,
) as ExtendedSelectKey[];

type FetchExtendedSelect = <T>(
  path: string,
  options?: { filters?: object },
) => Promise<T[]>;

/** Loads one extended select; preserves row type per registry key. */
export async function loadExtendedSelect<K extends ExtendedSelectKey>(
  key: K,
  fetch: FetchExtendedSelect,
): Promise<ExtendedSelectRowMap[K][]> {
  const config = extendedSelectConfig[key];
  return fetch<ExtendedSelectRowMap[K]>(config.path, {
    filters: config.filters,
  });
}
